import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useMediaQuery, useTheme } from "@mui/material"
import React, { useContext, useEffect, useState } from "react";

import { api, IInspectedAreaList, InspectedArea, Inspection, RequestStatus } from "../../../logic/api";

import flex from "../../../styles/flex";

import { getFileUrl } from "../../../utility/utils";
import { useDate } from "../../../utility/useDate";
import { SessionContext } from "../../../utility/useSession";

import { PagedDataView } from "../../../components/lists/PagedDataView/PagedDataView";
import NotchedCard from "../../../components/cards/NotchedCard/NotchedCard";

import { InspectionImage } from "./InspectionImage";
import { InspectionZoneViewer, groupAreasByZone, NEW_VIEWER_MIN_PER_ZONE } from "./InspectionZoneViewer";
import { useTranslation } from "react-i18next";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import JSZip from "jszip";
import saveAs from "file-saver";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";

export const BookingInspection: React.FunctionComponent<{
  inspection: Inspection,
  show: boolean,
  onHide: (saved: boolean) => void,
}> = (props) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { toFormattedDateTimeString, toFormattedFullDateTimeString } = useDate();

  const releaseInspectionZoneViewer = useReleaseManagement(ReleaseFlag.releaseInspectionZoneViewer);
  const releaseDownloadAllInspectionImages = useReleaseManagement(ReleaseFlag.releaseDownloadAllInspectionImages);

  const [inspection, setInspection] = useState<Inspection>(props.inspection);
  const [didSave, setDidSave] = useState<boolean>(false);
  const [inspectionParams, setInspectionsParams] = useState<IInspectedAreaList>({ inspectionId: props.inspection.id });

  const listInspectedAreas = api.hooks.inspection.listInspectedAreas();
  const [areas, setAreas] = useState<InspectedArea[]>([]);
  const [areasLoaded, setAreasLoaded] = useState(false);
  const [areasInspectionId, setAreasInspectionId] = useState<number | null>(null);
  const [showImageDownloadProgress, setShowImageDownloadProgress] = useState(false);
  const [imageDownloadErr, setImageDownloadErr] = useState<string | null>(null);

  const getUrl = (fileName: string) => getFileUrl(session, fileName);

  useEffect(() => {
    setInspection({ ...props.inspection });
    setInspectionsParams({ inspectionId: props.inspection.id });
    setDidSave(false);
    setShowImageDownloadProgress(false);
    setImageDownloadErr(null);
  }, [props.inspection]);

  // Fetch area metadata upfront ONLY when the new-viewer flag is on — needed to count photos and
  // decide whether to switch viewers. Flag off means this never runs and the legacy path is untouched.
  useEffect(() => {
    if (!props.show || !releaseInspectionZoneViewer) {
      setAreas([]);
      setAreasLoaded(false);
      setAreasInspectionId(null);
      return;
    }
    setAreasLoaded(false);
    setAreasInspectionId(null);
    listInspectedAreas.send(session.current.context, { inspectionId: props.inspection.id }, (result) => {
      setAreas(result ?? []);
      setAreasLoaded(true);
      setAreasInspectionId(props.inspection.id);
    });
  }, [props.show, props.inspection.id, releaseInspectionZoneViewer]);

  const areasReady = areasLoaded && areasInspectionId === props.inspection.id;
  const areasLoading = releaseInspectionZoneViewer && props.show && !areasReady && listInspectedAreas.status !== RequestStatus.error;

  // Route to the zone viewer when the busiest zone has enough photos to justify it. Sparse
  // (1-per-area) inspections stay on the legacy layout. Grouping/threshold live in the viewer.
  const currentAreas = areasReady ? areas : [];
  const { maxPerZone } = groupAreasByZone(currentAreas);
  const useNewViewer = releaseInspectionZoneViewer && maxPerZone > NEW_VIEWER_MIN_PER_ZONE;

  // New-path download: uses the already-fetched areas.
  const downloadInspectionImagesNew = async () => {
    if (currentAreas.length === 0) return;
    setShowImageDownloadProgress(true);
    const zip = new JSZip();
    try {
      await Promise.all(currentAreas.map(async (area, i) => {
        const response = await fetch(getUrl(area.photoFileName));
        if (!response.ok) throw new Error(response.statusText);
        const stamp = toFormattedFullDateTimeString(new Date(area.inspectionDate)).replace(/\\|\/|:| /g, "_");
        zip.file(`${stamp}_${i}.jpg`, await response.blob());
      }));
      saveAs(await zip.generateAsync({ type: "blob" }), `inspection_${inspection.id}_images.zip`);
    } catch (error) {
      setImageDownloadErr(`${error}`);
    }
    setShowImageDownloadProgress(false);
  };

  // Legacy download: self-fetches areas (original behaviour, unchanged).
  const downloadInspectionImages = async (inspectionId: number) => {
    listInspectedAreas.send(session.current.context, {
      inspectionId: inspectionId
    }, async (inspectedAreas) => {
      if (!inspectedAreas || inspectedAreas.length === 0) {
        console.warn("No inspected areas found for inspection:", inspectionId);
        return;
      }

      setShowImageDownloadProgress(true);
      const zip = new JSZip();

      const imageFetchPromises = inspectedAreas.map(async (inspectedArea, inspectedAreaIndex) => {
        const response = await fetch(getUrl(inspectedArea.photoFileName));
        if (response.ok === false) { throw new Error(`${response.statusText}`); }
        const blob = await response.blob();
        const inspectionDateFormatted = toFormattedFullDateTimeString(new Date(inspectedArea.inspectionDate))
          .replace(/\\|\/|:| /g, "_");
        zip.file(`${inspectionDateFormatted}_${inspectedAreaIndex}.jpg`, blob);
      });

      try {
        await Promise.all(imageFetchPromises);
        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, `inspection_${inspectionId}_images.zip`);
      } catch (error) {
        setImageDownloadErr(`${error}`);
      }
      setShowImageDownloadProgress(false);
    });
  };

  const mileageUrl = inspection.mileagePhoto ? getUrl(inspection.mileagePhoto) : null;
  const fuelUrl = inspection.fuelPhoto ? getUrl(inspection.fuelPhoto) : null;

  if (areasLoading) {
    return <Dialog open={props.show} fullWidth={true} maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>
        <div style={{ textAlign: "center" }}>
          {t("booking.labels.vehicleInspection")}
        </div>
      </DialogTitle>
      <DialogContent>
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <CircularProgress />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => props.onHide(didSave)} color="secondary">
          {t("general.close")}
        </Button>
      </DialogActions>
    </Dialog>;
  }

  // ============================ Grid Viewer ============================
  if (useNewViewer) {
    return <Dialog open={props.show} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>
        <div style={{ textAlign: "center" }}>
          {t("booking.labels.vehicleInspection")}
        </div>
      </DialogTitle>

      <DialogContent>
        {showImageDownloadProgress && <ProgressDialog status={RequestStatus.loading}></ProgressDialog>}
        {imageDownloadErr != null && <ErrorDialog err={imageDownloadErr} onClose={() => setImageDownloadErr(null)}></ErrorDialog>}
        <ErrorDialog err={listInspectedAreas.err}></ErrorDialog>

        <div style={{ marginBottom: 20 }}>
          <div style={flex.flexRows}>
            <div style={{ ...flex.flexFill, textAlign: "right" }}>
              <div style={{ paddingRight: 20 }}>{t("general.created")}:</div>
            </div>
            <div style={flex.flexFill}>
              {toFormattedDateTimeString(new Date(inspection.inspectionDate))}
            </div>
          </div>
          <div style={{ height: 5 }}></div>
          <div style={flex.flexRows}>
            <div style={{ ...flex.flexFill, textAlign: "right" }}>
              <div style={{ paddingRight: 20 }}>{t("booking.labels.loggedBy")}:</div>
            </div>
            <div style={flex.flexFill}>
              {inspection.createdBy.firstName} {inspection.createdBy.lastName}
            </div>
          </div>
          <div style={{ height: 5 }}></div>
          {(inspection.deviceSerialNo != null && inspection.deviceSerialNo != "") && (
            <div style={flex.flexRows}>
              <div style={{ ...flex.flexFill, textAlign: "right" }}>
                <div style={{ paddingRight: 20 }}>{t("booking.labels.device")}:</div>
              </div>
              <div style={flex.flexFill}>
                {inspection.deviceSerialNo}
              </div>
            </div>
          )}
        </div>

        <NotchedCard label={t("booking.labels.mileagePhoto")}>
          <div style={{ textAlign: "center" }}>
            {mileageUrl
              ? <img style={{ width: "100%", borderRadius: 10, display: "inline-block" }} src={mileageUrl} alt={t("booking.labels.mileagePhoto")} />
              : <Typography variant="body2" color="text.disabled">{t("input.validations.noMileagePhoto")}</Typography>}
          </div>
        </NotchedCard>
        <NotchedCard label={t("booking.labels.fuelPhoto")}>
          <div style={{ textAlign: "center" }}>
            {fuelUrl
              ? <img style={{ width: "100%", borderRadius: 10, display: "inline-block" }} src={fuelUrl} alt={t("booking.labels.fuelPhoto")} />
              : <Typography variant="body2" color="text.disabled">{t("input.validations.noFuelPhoto")}</Typography>}
          </div>
        </NotchedCard>

        <NotchedCard label={t("booking.labels.vehiclePhotos")}>
          <InspectionZoneViewer areas={currentAreas} getUrl={getUrl} />
        </NotchedCard>
      </DialogContent>

      <DialogActions>
        {releaseDownloadAllInspectionImages && currentAreas.length > 0 && (
          <Button onClick={downloadInspectionImagesNew} color="secondary">
            {t("general.downloadAll")}
          </Button>)}
        <Button onClick={() => props.onHide(false)} color="secondary">
          {t("general.close")}
        </Button>
      </DialogActions>
    </Dialog>;
  }

  // ============================ List Viewer ============================
  return <Dialog open={props.show} fullWidth={true} maxWidth="md"  >
    <DialogTitle>
      <div style={{ textAlign: "center" }}>
        {t("booking.labels.vehicleInspection")}
      </div>
    </DialogTitle>
    <DialogContent>
      {showImageDownloadProgress == true && <ProgressDialog status={RequestStatus.loading}></ProgressDialog>}
      {imageDownloadErr != null && <ErrorDialog err={imageDownloadErr}></ErrorDialog>}

      <ErrorDialog err={listInspectedAreas.err} onClose={() => setImageDownloadErr(null)}></ErrorDialog>
      {/* flag on but photos still loading: show progress instead of flashing the legacy grid */}
      {areasLoading
        ? <ProgressDialog status={RequestStatus.loading}></ProgressDialog>
        : <ProgressDialog status={listInspectedAreas.status}></ProgressDialog>}
      <div style={{ marginBottom: 40 }}>
        <div style={flex.flexRows}>
          <div style={{ ...flex.flexFill, textAlign: "right" }}>
            <div style={{ paddingRight: 20 }}>{t("general.created")}:</div>
          </div>
          <div style={flex.flexFill}>
            {toFormattedDateTimeString(new Date(inspection.inspectionDate))}
          </div>
        </div>
        <div style={{ height: 5 }}></div>
        <div style={flex.flexRows}>
          <div style={{ ...flex.flexFill, textAlign: "right" }}>
            <div style={{ paddingRight: 20 }}>{t("booking.labels.loggedBy")}:</div>
          </div>
          <div style={flex.flexFill}>
            {inspection.createdBy.firstName} {inspection.createdBy.lastName}
          </div>
        </div>

        <div style={{ height: 5 }}></div>
        {(inspection.deviceSerialNo != null && inspection.deviceSerialNo != "") && (
          <div style={flex.flexRows}>
            <div style={{ ...flex.flexFill, textAlign: "right" }}>
              <div style={{ paddingRight: 20 }}>{t("booking.labels.device")}:</div>
            </div>
            <div style={flex.flexFill}>
              {inspection.deviceSerialNo}
            </div>
          </div>
        )}
        <div style={{ height: 20 }}></div>
        <NotchedCard label={t("booking.labels.mileagePhoto")} >
          <div style={{ textAlign: "center" }}>
            {inspection.mileagePhoto == null || inspection.mileagePhoto == "" && (<div>
              {t("input.validations.noMileagePhoto")}
            </div>)}
            {inspection.mileagePhoto != null && inspection.mileagePhoto != "" && (<div>

              <img style={{ width: "100%", display: "inline-block", borderRadius: 10, }} src={getFileUrl(session, inspection.mileagePhoto)} />
              <div>{toFormattedDateTimeString(new Date(inspection.mileagePhoto))}</div>
            </div>)}
          </div>
        </NotchedCard>
        <NotchedCard label={t("booking.labels.fuelPhoto")} >
          <div style={{ textAlign: "center" }}>
            {inspection.fuelPhoto == null || inspection.fuelPhoto == "" && (<div>
              {t("input.validations.noFuelPhoto")}
            </div>)}
            {inspection.fuelPhoto != null && inspection.fuelPhoto != "" && (<div>
              <img style={{ width: "100%", display: "inline-block", borderRadius: 10, }} src={getFileUrl(session, inspection.fuelPhoto)} />
              <div>{toFormattedDateTimeString(new Date(inspection.fuelPhoto))}</div>
            </div>)}
          </div>
        </NotchedCard>
        <NotchedCard label={t("booking.labels.vehiclePhotos")} >

          <PagedDataView<IInspectedAreaList, InspectedArea>
            apiHook={api.hooks.inspection.listInspectedAreas}
            parameters={inspectionParams}
            renderItem={(item, index) => {

              return <div
                style={{ paddingTop: index == 0 ? 50 : 0 }}
              >
                <InspectionImage inspectedArea={item} url={getFileUrl(session, item.photoFileName)}></InspectionImage>
              </div>
            }}
          >
            {t("input.validations.noVehiclePhoto")}
          </PagedDataView>
        </NotchedCard>
      </div>


    </DialogContent>
    <DialogActions>
      {releaseDownloadAllInspectionImages && (<Button onClick={() => downloadInspectionImages(inspection.id)} color="secondary">
        {t("general.downloadAll")}
      </Button>)}
      <Button onClick={() => props.onHide(didSave)} color="secondary">
        {t("general.close")}
      </Button>
    </DialogActions>
  </Dialog>
};
